import React, { useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  CssBaseline,
  ThemeProvider,
  createTheme,
  keyframes,
  useMediaQuery,
  Divider
} from '@mui/material';
import { Helmet } from 'react-helmet';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#28282B',
      paper: 'rgba(26, 26, 26, 0.8)',
    },
    text: {
      primary: '#FFFFFF',
    },
    primary: {
      main: '#FFD700',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: 'linear-gradient(180deg, #28282B 0%, #1A1A1A 100%)',
        },
      },
    },
  },
});

const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const gradientBackground = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const AboutCrowdlaunch = () => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const particlesInit = async (engine) => {
    await loadSlim(engine);
  };

  // Track page view on component mount.
  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-W97PQD73RV', {
        page_path: window.location.pathname,
      });
    }
  }, []);

  // Section header component for consistent styling.
  const SectionHeader = ({ children }) => (
    <Typography
      variant="h4"
      component="h2"
      sx={{
        background: 'linear-gradient(45deg, #FFD700, #FFEC8B)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        mb: 3,
        fontWeight: 700,
        position: 'relative',
        '&:after': {
          content: '""',
          position: 'absolute',
          bottom: -8,
          left: 0,
          width: '60px',
          height: '3px',
          background: '#FFD700',
        },
      }}
    >
      {children}
    </Typography>
  );

  // FeatureCard for benefit boxes.
  const FeatureCard = ({ children, title }) => (
    <Box
      sx={{
        background: 'rgba(26, 26, 26, 0.5)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        p: 3,
        mb: 3,
        border: '1px solid rgba(255, 215, 0, 0.1)',
        transition: 'transform 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
        },
      }}
    >
      <Typography variant="h6" sx={{ color: '#FFD700', mb: 1.5, fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
        {children}
      </Typography>
    </Box>
  );

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Helmet>
        <title>Learn – Project Tokenization & Share Marketplace</title>
        <meta
          name="description"
          content="Learn what project tokenization is, how to tokenize your project, buy tokenized shares, and use our share marketplace where holders set the price. Discover the benefits and processes behind decentralized project financing."
        />
        <meta
          name="keywords"
          content="CrowdLaunch, project tokenization, tokenized shares, soft cap, hard cap, dividends, share marketplace, blockchain, crypto"
        />
        {/* Global Site Tag (gtag.js) - Google Analytics */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-W97PQD73RV"></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-W97PQD73RV', { page_path: window.location.pathname });
          `}
        </script>
      </Helmet>
      <Box sx={{ position: 'relative', minHeight: '100vh' }}>
        <Particles
          init={particlesInit}
          options={{
            particles: {
              number: { value: 80 },
              color: { value: "#FFD700" },
              opacity: { value: 0.4 },
              size: { value: 2 },
              move: { enable: true, speed: 1.5 },
            },
            interactivity: {
              events: {
                onHover: { enable: true, mode: 'repulse' },
              },
            },
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: -1,
          }}
        />

        <Container maxWidth="lg" sx={{ py: 8 }}>
          {/* Hero Section */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: isMobile ? '2.5rem' : '4rem',
                fontWeight: 800,
                background: 'linear-gradient(45deg, #FFD700, #FFFFFF)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
                animation: `${floatAnimation} 3s ease-in-out infinite`,
              }}
            >
              Revolutionizing Project Funding
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                maxWidth: '800px',
                mx: 'auto',
                mb: 4,
              }}
            >
              Decentralized tokenization platform for next-generation fundraising
            </Typography>
          </Box>

          {/* What is Project Tokenization & Key Benefits */}
          <Grid container spacing={4} sx={{ mb: 8 }}>
            <Grid item xs={12} md={6}>
              <Box sx={{ pr: { md: 4 } }}>
                <SectionHeader>What is Project Tokenization?</SectionHeader>
                <Typography
                  variant="body1"
                  sx={{
                    marginBottom: '30px',
                    color: 'rgba(255,255,255,0.9)',
                    lineHeight: 1.8,
                  }}
                >
                  Transform your project's value into digital tokens on the blockchain, enabling fractional ownership,
                  global accessibility, and transparent governance through decentralized technology.
                </Typography>
                <SectionHeader>Why Tokenize Your Project?</SectionHeader>
                <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
                  Tokenization streamlines fundraising and democratizes ownership. It allows you to:
                </Typography>
                <Box component="ul" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', ml: 4, mt: 2 }}>
                  <li>Access a broader investor base worldwide.</li>
                  <li>Secure funding with transparent, immutable records.</li>
                  <li>Enable fractional ownership, making investment more affordable.</li>
                  <li>Automate dividend and governance processes using smart contracts.</li>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  background: 'linear-gradient(45deg, rgba(255,215,0,0.1), rgba(255,215,0,0.05))',
                  borderRadius: '16px',
                  p: 3,
                  height: '100%',
                }}
              >
                <SectionHeader>Key Benefits</SectionHeader>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FeatureCard title="Global Access">
                      Raise capital from investors worldwide with crypto-native infrastructure.
                    </FeatureCard>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FeatureCard title="Liquidity">
                      Access to decentralized liquidity through holder-driven pricing.
                    </FeatureCard>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FeatureCard title="Transparency">
                      Immutable blockchain records ensure full auditability in real time.
                    </FeatureCard>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FeatureCard title="Automation">
                      Smart contracts automatically distribute dividends and manage governance.
                    </FeatureCard>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>

          {/* How It Works Section */}
          <Box sx={{ mb: 8 }}>
            <SectionHeader>How It Works</SectionHeader>
            <Grid container spacing={4} sx={{ mt: 2 }}>
              <Grid item xs={12} md={4}>
                <FeatureCard title="1. Tokenize">
                  Convert your project into tradeable digital assets with customizable parameters.
                </FeatureCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <FeatureCard title="2. Launch">
                  Distribute tokenized shares through our decentralized fundraising platform.
                </FeatureCard>
              </Grid>
              <Grid item xs={12} md={4}>
                <FeatureCard title="3. Grow">
                  Manage investor relations and distribute dividends through smart contracts.
                </FeatureCard>
              </Grid>
            </Grid>
          </Box>

          {/* How to Tokenize Your Project */}
          <Box sx={{ mb: 8 }}>
            <SectionHeader>How to Tokenize Your Project</SectionHeader>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
              To tokenize your project on CrowdLaunch, follow these steps:
            </Typography>
            <Box component="ol" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', ml: 4, mt: 2 }}>
              <li>
                Go to&nbsp;
                <a
                  href="https://app.crowdlaunch.xyz/tokenize"
                  style={{ color: darkTheme.palette.primary.main, textDecoration: 'underline' }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  app.crowdlaunch.xyz/tokenize
                </a>.
              </li>
              <li>Click the <strong>"Tokenize"</strong> button.</li>
              <li>
                Fill in your funding requirements:
                <ul style={{ marginLeft: '20px' }}>
                  <li>
                    <strong>SoftCap:</strong> The minimum amount your project must raise. If not reached, investors can claim refunds.
                  </li>
                  <li>
                    <strong>HardCap:</strong> The maximum funding target for your project.
                  </li>
                </ul>
              </li>
              <li>Click <strong>"Tokenize"</strong> to begin the process.</li>
              <li>
                Approve the spending in your wallet and confirm the transaction.
              </li>
              <li>Your tokenized project will appear immediately on your dashboard.</li>
            </Box>
          </Box>

          {/* How to Buy Tokenized Shares */}
          <Box sx={{ mb: 8 }}>
            <SectionHeader>How to Buy Tokenized Shares</SectionHeader>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
              To invest in a tokenized project:
            </Typography>
            <Box component="ol" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', ml: 4, mt: 2 }}>
              <li>Connect your wallet to CrowdLaunch.</li>
              <li>Browse the Share Marketplace.</li>
              <li>Select a project and click the <strong>"Buy Shares"</strong> option.</li>
              <li>Enter your desired investment amount and confirm the transaction in your wallet.</li>
            </Box>
          </Box>

          {/* Why Buy Tokenized Shares */}
          <Box sx={{ mb: 8 }}>
            <SectionHeader>Why Buy Tokenized Shares?</SectionHeader>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
              Purchasing tokenized shares lets you share in a project's growth. Benefits include:
            </Typography>
            <Box component="ul" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '1rem', ml: 4, mt: 2 }}>
              <li>Potential for capital appreciation as the project succeeds.</li>
              <li>Regular dividend distributions based on project performance.</li>
              <li>Easy transferability of shares along with attached dividend rights.</li>
            </Box>
          </Box>

          {/* Dividend Distribution */}
          <Box sx={{ mb: 8 }}>
            <SectionHeader>Dividend Distribution</SectionHeader>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
              Project owners deposit dividends into a smart contract that automatically distributes funds to all share holders
              based on the number of shares they hold. The dividend rights remain attached to the share—even if it is transferred.
            </Typography>
          </Box>

          {/* Transferability */}
          <Box sx={{ mb: 8 }}>
            <SectionHeader>Are Tokenized Shares Transferable?</SectionHeader>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
              Yes. Tokenized shares can be freely transferred between wallets. Moreover, the dividend rights that accompany each share travel with it,
              ensuring that your investment benefits continue even after a sale.
            </Typography>
          </Box>

          {/* Share Marketplace */}
          <Box sx={{ mb: 8 }}>
            <SectionHeader>Share Marketplace</SectionHeader>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.8 }}>
              In the Share Marketplace, holders set their own prices for tokenized shares. Instead of relying solely on market supply and demand,
              prices are determined by the individual seller’s valuation. This allows for a more personalized and flexible investment environment.
            </Typography>
          </Box>

          {/* Final CTA */}
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => window.open('https://app.crowdlaunch.xyz', '_blank')}
              sx={{
                px: 6,
                py: 2,
                fontWeight: 700,
                borderRadius: '16px',
                background: 'linear-gradient(45deg, #FFD700, #FFA500)',
                color: '#1A1A1A',
                fontSize: '1.1rem',
                '&:hover': {
                  background: 'linear-gradient(45deg, #FFC107, #FF8C00)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 8px 24px rgba(255,215,0,0.4)',
                },
              }}
            >
              Launch Your Project Now
            </Button>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default AboutCrowdlaunch;
